import { useEffect } from 'react';

export default function SEOSchema() {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://physio-muehlbacher.at';

    const localBusinessSchema = {
      '@context': 'https://schema.org',
      '@type': ['MedicalBusiness', 'LocalBusiness'],
      '@id': `${siteUrl}/#business`,
      name: 'Physiotherapie Theresa Mühlbacher',
      description:
        'Physiotherapeutische Praxis in Munderfing (OÖ) mit Schwerpunkten auf Rückenschmerzen, Gelenkschmerzen, Beckenbodentraining, Cranio Sacrale Therapie und viszeraler Therapie.',
      url: siteUrl,
      telephone: '+43-660-3450403',
      email: 'Theresa.weiss1991@gmail.com',
      image:
        'https://storage.readdy-site.link/project_files/282f4fb1-17a3-40b7-81fd-9ffa411cf374/40d3b560-fb8a-43a8-a511-dcc5e930f4fd_Foto-2-von-21.jpg?v=0cf56b189c78fc597a3e4dda4989aa68',
      logo: 'https://static.readdy.ai/image/a2fa61b06b87a0c1b7ab810b11b77ac9/500be7c3a1a7b1506d81a8520e3a5b53.png',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Achenlohe 13',
        addressLocality: 'Munderfing',
        postalCode: '5222',
        addressCountry: 'AT',
        addressRegion: 'Oberösterreich',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 48.0667,
        longitude: 13.5167,
      },
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: 'Tuesday',
          opens: '08:00',
          closes: '20:00',
        },
      ],
      priceRange: '€€',
      currenciesAccepted: 'EUR',
      paymentAccepted: 'Cash, Bank Transfer',
      areaServed: {
        '@type': 'GeoCircle',
        geoMidpoint: {
          '@type': 'GeoCoordinates',
          latitude: 48.0667,
          longitude: 13.5167,
        },
        geoRadius: '30000',
      },
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Physiotherapeutische Leistungen',
        itemListElement: [
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'MedicalTherapy',
              name: 'Rückenschmerzen',
              description:
                'Behandlung akuter und chronischer Wirbelsäulenbeschwerden – von Hexenschuss über Bandscheibenvorfälle bis hin zu muskulären Verspannungen.',
            },
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'MedicalTherapy',
              name: 'Gelenkschmerzen',
              description:
                'Linderung von Arthrose, Rheuma und altersbedingtem Verschleiß; Verbesserung der Gelenkfunktion.',
            },
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'MedicalTherapy',
              name: 'Knie- und Schulterschmerzen',
              description:
                'Gezielte Mobilisation und Kräftigung nach Verletzungen, Überlastung oder Operationen an Knie und Schulter.',
            },
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'MedicalTherapy',
              name: 'Beckenbodentraining / Inkontinenz',
              description:
                'Gezieltes Beckenbodentraining bei Blasen- oder Darmschwäche für Frauen und Männer.',
            },
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'MedicalTherapy',
              name: 'Cranio Sacrale Therapie',
              description:
                'Ganzheitliche Behandlung nach Upledger (CST I & II) für Körper und Geist.',
            },
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'MedicalTherapy',
              name: 'Atemtherapie',
              description:
                'Unterstützung bei chronischen Atemwegserkrankungen (Asthma, COPD) sowie nach Operationen.',
            },
          },
        ],
      },
      sameAs: ['https://www.instagram.com'],
    };

    const personSchema = {
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: 'Theresa Mühlbacher',
      jobTitle: 'Physiotherapeutin',
      worksFor: {
        '@id': `${siteUrl}/#business`,
      },
      knowsAbout: [
        'Physiotherapie',
        'Manuelle Therapie',
        'Cranio Sacrale Therapie',
        'Viszerale Therapie',
        'Beckenbodentraining',
        'Atemtherapie',
        'Kinesiologisches Taping',
        'Nordic Walking',
      ],
    };

    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Was soll ich zur Physiotherapie mitbringen?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Bitte bringe zu jeder Behandlung ein eigenes großes Handtuch (z. B. ein Saunatuch) mit.',
          },
        },
        {
          '@type': 'Question',
          name: 'Wie kann ich einen Termin bei Theresa Mühlbacher buchen?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Du kannst einen Termin ganz einfach online über das Buchungsformular auf der Website buchen – 24/7 verfügbar.',
          },
        },
        {
          '@type': 'Question',
          name: 'Bis wann muss ich einen Termin absagen?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Bitte sage einen Termin mindestens 24 Stunden vorher ab. Nicht rechtzeitig abgesagte Termine werden in voller Höhe in Rechnung gestellt.',
          },
        },
        {
          '@type': 'Question',
          name: 'Wo befindet sich die Physiotherapiepraxis in Munderfing?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Die Praxis befindet sich in Achenlohe 13, 5222 Munderfing, Österreich. Direkt vor Ort stehen zwei Parkplätze rechts vom Carport zur Verfügung.',
          },
        },
        {
          '@type': 'Question',
          name: 'Welche Behandlungen bietet die Physiotherapie Theresa Mühlbacher an?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Das Leistungsangebot umfasst: Rückenschmerzen, Gelenkschmerzen, Knie- und Schulterschmerzen, Beckenbodentraining bei Inkontinenz, Kopfschmerzen und Tinnitus, Atemtherapie sowie Cranio Sacrale und Viszerale Therapie.',
          },
        },
      ],
    };

    const schemas = [localBusinessSchema, personSchema, faqSchema];

    const scriptTags: HTMLScriptElement[] = schemas.map((schema, i) => {
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.id = `schema-physio-${i}`;
      el.textContent = JSON.stringify(schema);
      document.head.appendChild(el);
      return el;
    });

    return () => {
      scriptTags.forEach((el) => {
        if (document.head.contains(el)) {
          document.head.removeChild(el);
        }
      });
    };
  }, []);

  return null;
}
